import { Badge, Button, Card, CardContent, Input, Label, Spinner } from '@lensora/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'


import { useMe } from '@/hooks/use-me'
import { api, ApiError } from '@/lib/api'

type AdminProfile = {
  id: string
  email: string | null
  fullName: string | null
  role: string
  isLensoraAdmin: boolean
  tenantId: string
  tenantName: string
  tenantSlug: string
  isActive: boolean
  createdAt: string
}

type TenantOption = { id: string; name: string; slug: string }

const roleVariant: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  owner: 'default',
  admin: 'secondary',
  member: 'outline',
  client: 'outline',
}

const ROLES = ['owner', 'admin', 'member'] as const

function AddUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [email, setEmail] = React.useState('')
  const [fullName, setFullName] = React.useState('')
  const [tenantId, setTenantId] = React.useState('')
  const [role, setRole] = React.useState<typeof ROLES[number]>('member')
  const [error, setError] = React.useState<string | null>(null)
  const [inviteUrl, setInviteUrl] = React.useState<string | null>(null)

  const { data: tenants, isLoading: loadingTenants } = useQuery({
    queryKey: ['lensora-admin', 'tenants'],
    queryFn: () => api.get<TenantOption[]>('/api/lensora-admin/tenants'),
  })

  const mutation = useMutation({
    mutationFn: (data: { email: string; fullName: string; tenantId?: string; role: string }) =>
      api.post<{ userId: string; inviteUrl: string | null }>('/api/lensora-admin/users', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lensora-admin', 'users'] }),
  })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      const result = await mutation.mutateAsync({
        email,
        fullName,
        role,
        ...(tenantId ? { tenantId } : {}),
      })
      if (result.inviteUrl) {
        setInviteUrl(result.inviteUrl)
      } else {
        onClose()
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create user')
    }
  }

  if (inviteUrl) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-lg space-y-4">
          <h2 className="text-lg font-semibold text-text">User created</h2>
          <p className="text-sm text-text-muted">
            Send this link to <strong>{email}</strong>. They click it to set their password
            and are taken straight to the dashboard. The link expires after 24 hours.
            {!tenantId && ' Note: they won\'t have dashboard access until assigned to a tenant.'}
          </p>
          <div className="rounded-md bg-surface-raised p-3 break-all font-mono text-xs text-text select-all">
            {inviteUrl}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => { navigator.clipboard.writeText(inviteUrl) }}
            >
              Copy link
            </Button>
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-lg">
        <h2 className="mb-4 text-lg font-semibold text-text">Add user</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="u-email">Email</Label>
            <Input
              id="u-email"
              type="email"
              placeholder="user@company.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="u-name">Full name</Label>
            <Input
              id="u-name"
              placeholder="Jane Smith"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="u-tenant">
                Tenant
                <span className="ml-1 text-text-muted font-normal">(optional)</span>
              </Label>
              <select
                id="u-tenant"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                disabled={loadingTenants}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50"
              >
                <option value="">{loadingTenants ? 'Loading…' : 'No tenant yet'}</option>
                {tenants?.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-role">
                Role
                {!tenantId && <span className="ml-1 text-text-muted font-normal">(assigned on provision)</span>}
              </Label>
              <select
                id="u-role"
                value={role}
                disabled={!tenantId}
                onChange={(e) => setRole(e.target.value as typeof ROLES[number])}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r} className="capitalize">{r}</option>
                ))}
              </select>
            </div>
          </div>
          {error && <p className="text-sm text-feedback-error">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create user'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function AdminUsersPage() {
  const { data: me } = useMe()
  const [searchParams] = useSearchParams()
  const tenantFilter = searchParams.get('tenantId')
  const [search, setSearch] = React.useState('')
  const [showAddUser, setShowAddUser] = React.useState(false)
  const queryClient = useQueryClient()

  const { data: users, isLoading } = useQuery({
    queryKey: ['lensora-admin', 'users'],
    queryFn: () => api.get<AdminProfile[]>('/api/lensora-admin/users'),
  })

  const toggleAdminMutation = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      api.patch(`/api/lensora-admin/users/${id}/lensora-admin`, { value }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lensora-admin', 'users'] }),
  })

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, tenantId, disabled }: { id: string; tenantId: string; disabled: boolean }) =>
      api.patch(`/api/lensora-admin/users/${id}/status`, { disabled, tenantId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lensora-admin', 'users'] }),
  })

  const filtered = React.useMemo(() => {
    if (!users) return []
    return users.filter((u) => {
      if (tenantFilter && u.tenantId !== tenantFilter) return false
      if (search) {
        const q = search.toLowerCase()
        return (
          u.email?.toLowerCase().includes(q) ||
          u.fullName?.toLowerCase().includes(q) ||
          u.tenantName.toLowerCase().includes(q)
        )
      }
      return true
    })
  }, [users, tenantFilter, search])

  if (isLoading) {
    return <div className="flex h-40 items-center justify-center"><Spinner /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text">Users</h1>
          <p className="mt-1 text-sm text-text-muted">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="search"
            placeholder="Search by name, email or tenant…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
          <Button onClick={() => setShowAddUser(true)}>Add user</Button>
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map((u) => (
          <Card key={u.id}>
            <CardContent className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-accent text-xs font-medium text-text-inverse">
                  {(u.email ?? '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text">{u.fullName ?? u.email ?? 'Unknown'}</p>
                  <p className="truncate text-xs text-text-muted">{u.email} · {u.tenantName}</p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {u.isLensoraAdmin && <Badge variant="default" className="text-xs">Lensora Admin</Badge>}
                <Badge variant={roleVariant[u.role] ?? 'outline'} className="capitalize text-xs">{u.role}</Badge>
                {!u.isActive && <Badge variant="destructive" className="text-xs">Disabled</Badge>}

                {u.id !== me?.user.id && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      disabled={toggleAdminMutation.isPending}
                      onClick={() => toggleAdminMutation.mutate({ id: u.id, value: !u.isLensoraAdmin })}
                    >
                      {u.isLensoraAdmin ? 'Revoke admin' : 'Grant admin'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      disabled={toggleStatusMutation.isPending}
                      onClick={() => toggleStatusMutation.mutate({ id: u.id, tenantId: u.tenantId, disabled: u.isActive })}
                    >
                      {u.isActive ? 'Disable' : 'Enable'}
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showAddUser && <AddUserModal onClose={() => setShowAddUser(false)} />}
    </div>
  )
}
